import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Eye, EyeOff, LogIn, Zap, Shield, Activity, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
  { icon: Activity, text: "Real-time call monitoring & transcripts" },
  { icon: Shield, text: "HIPAA-aligned patient data management" },
  { icon: Clock, text: "24/7 AI receptionist performance analytics" },
];

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem("clinic_admin_token", data.access_token);
      localStorage.setItem("clinic_admin_user", data.username);
      localStorage.setItem("clinic_id", data.clinic_id || "default");
      localStorage.setItem("clinic_name", data.clinic_name || "Careline");
      localStorage.setItem("onboarding_completed", data.onboarding_completed ? "true" : "false");
      toast.success("Welcome back!");
      if (data.onboarding_completed === false) {
        navigate("/onboarding");
      } else {
        navigate("/admin");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0D14] flex overflow-hidden relative">
      {/* Animated background gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-cyan-500/5 blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-violet-500/5 blur-3xl animate-pulse" style={{ animationDelay: "1.5s" }} />
        <div className="absolute top-1/2 left-1/3 w-[400px] h-[400px] rounded-full bg-cyan-500/3 blur-3xl animate-pulse" style={{ animationDelay: "3s" }} />
      </div>

      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative border-r border-white/5">
        {/* Subtle grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.3) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,.3) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />

        <div className="relative">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold font-outfit text-white text-xl tracking-tight">Careline</span>
          </div>

          <div className="space-y-6">
            <div>
              <h1 className="text-4xl xl:text-5xl font-bold font-outfit text-white leading-tight">
                The AI Receptionist for{" "}
                <span className="bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent">
                  Modern Healthcare
                </span>
              </h1>
              <p className="text-slate-400 mt-4 text-lg leading-relaxed">
                Manage your clinic's AI-powered communication hub. Monitor calls, patients, and appointments from one unified dashboard.
              </p>
            </div>

            <div className="space-y-4 pt-2">
              {features.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-cyan-400" />
                  </div>
                  <span className="text-slate-300 text-sm">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom decoration */}
        <div className="relative">
          <div className="flex items-center gap-2 text-slate-600 text-xs">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            System operational · All services running
          </div>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2.5 mb-10">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
            <Zap className="w-4.5 h-4.5 text-white" style={{ width: 18, height: 18 }} />
          </div>
          <span className="font-bold font-outfit text-white text-lg">Careline Admin</span>
        </div>

        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="text-2xl font-bold font-outfit text-white">Welcome back</h2>
            <p className="text-slate-400 text-sm mt-1">Sign in to your admin dashboard</p>
          </div>

          <form
            data-testid="admin-login-form"
            onSubmit={handleSubmit}
            className="bg-[#161B27] border border-white/10 rounded-2xl p-8 space-y-5 shadow-2xl shadow-black/40"
          >
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-1.5 uppercase tracking-wider">
                Username
              </label>
              <input
                data-testid="login-username-input"
                type="text"
                value={form.username}
                onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                placeholder="admin"
                required
                className="w-full px-4 py-2.5 bg-[#0A0D14] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-400 block mb-1.5 uppercase tracking-wider">
                Password
              </label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPass ? "text" : "password"}
                  value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="••••••••"
                  required
                  className="w-full px-4 py-2.5 pr-10 bg-[#0A0D14] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                />
                <button
                  type="button"
                  data-testid="toggle-password-btn"
                  onClick={() => setShowPass(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-semibold py-2.5 rounded-lg hover:from-cyan-400 hover:to-violet-500 disabled:opacity-50 transition-all duration-200 shadow-lg shadow-cyan-500/20 mt-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  Sign In
                </>
              )}
            </button>

            <p className="text-xs text-center text-slate-600 border-t border-white/5 pt-4">
              Credentials: admin / Careline2025!
            </p>
          </form>

          <div className="flex items-center gap-3 mt-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-slate-500 text-xs">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <div className="text-center mt-4">
            <span className="text-slate-400 text-sm">New to Careline? </span>
            <button
              type="button"
              onClick={() => navigate("/signup")}
              className="text-cyan-400 hover:text-cyan-300 text-sm transition-colors"
            >
              Create your clinic account →
            </button>
          </div>

          <div className="text-center mt-4">
            <a href="/" className="text-sm text-slate-500 hover:text-cyan-400 transition-colors">
              ← Back to Landing Page
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
